import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, ArrowRight, Sparkles, Activity, FileDown } from 'lucide-react';
import { heroContent, clientLogos, services, siteConfig } from '../data/mock';
import blogPosts from '../data/blogData.json';
import PageSeo from '../components/PageSeo';
import ResultsWall from '../components/ResultsWall';
import JsonLd, { organizationSchema } from '../components/JsonLd';
import { sectors } from '../data/sectors';
import { imgProps } from '../lib/img';
import { ResourceGateModal } from '../components/ResourceGate';

const SECTOR_ORDER = ['charities', 'care-technology', 'health-and-social-care', 'wellbeing', 'education-and-send', 'professional-services', 'tech-and-saas', 'e-commerce'];
const SECTOR_TAGLINES = {
  'charities': 'Donor acquisition, awareness and the Google Ad Grant, done responsibly.',
  'care-technology': 'Turn complex healthtech into clear propositions and real pipeline.',
  'health-and-social-care': 'More enquiries, better recruitment and a stronger reputation.',
  'wellbeing': 'Warm, compliant marketing that reaches people who need you.',
  'education-and-send': 'Admissions, course and staff recruitment campaigns that convert.',
  'professional-services': 'Authority-building marketing that wins better-quality enquiries.',
  'tech-and-saas': 'Cut through the noise and build a predictable, qualified pipeline.',
  'e-commerce': 'Store design, ads and email that turn traffic into profitable, repeat sales.',
};
const orderedHomeSectors = SECTOR_ORDER.map((sl) => sectors.find((s) => s.slug === sl)).filter(Boolean);

const BLOG_CATEGORIES = ['All', 'Care Homes', 'Home Care', 'Care Tech', 'AI & Innovation', 'Charities', 'Education'];

const useSectionReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add('visible'); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const SectionReveal = ({ children, style, className = '' }) => {
  const ref = useSectionReveal();
  return <div ref={ref} className={`section-reveal ${className}`} style={style}>{children}</div>;
};

const TikTokIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.71a8.19 8.19 0 004.76 1.52V6.78a4.83 4.83 0 01-1-.09z" />
  </svg>
);

/* Cactus background images from original site */
const cactusBgImages = [
  'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/wkinc7h7_cactus-wall-pink-no-bg.png',
  'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/5985xkhn_three-cacti-with-bg-pink.png',
  'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/eo4t0cej_cactus-standing-up-white-gold.png',
];
/* Service row bg alternates: yellow, white, yellow */
const serviceRowBgs = ['var(--yellow)', '#FFFFFF', 'var(--yellow)'];
/* Service title colors: dark, teal, dark (as in original) */
const serviceTitleColors = ['var(--dark)', 'var(--teal)', 'var(--dark)'];

const BlogPreviewSection = () => {
  const [activeCat, setActiveCat] = useState('All');
  const washColors = ['#3598AD', '#FD7AAB', '#3E4391', '#17879F'];
  const stockImgs = [
    'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80',
    'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&q=80',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
  ];
  const activeBlogPosts = blogPosts.filter(p => !p.archived);
  const filtered = activeCat === 'All' ? activeBlogPosts : activeBlogPosts.filter(p => p.category === activeCat);
  const displayed = filtered.slice(0, 4);

  return (
    <section style={{ background: 'var(--teal)', padding: '60px 0' }} data-testid="blog-section">
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 36 }}>
          {BLOG_CATEGORIES.map((cat) => {
            const isActive = activeCat === cat;
            return (
              <button
                key={cat}
                data-testid={`home-blog-filter-${cat.toLowerCase().replace(/[& ]/g, '-')}`}
                onClick={() => setActiveCat(cat)}
                style={{
                  padding: '8px 18px',
                  background: isActive ? 'var(--hot-pink)' : 'rgba(255,255,255,0.15)',
                  color: 'var(--white)',
                  border: `2px solid ${isActive ? 'var(--hot-pink)' : 'rgba(255,255,255,0.3)'}`,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '11px',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              >
                {cat}
              </button>
            );
          })}
        </div>
        <div className="blog-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 28 }}>
          {displayed.map((post, i) => (
            <div key={post.slug} style={{ textAlign: 'center' }}>
              <Link to={`/blog/${post.slug}`} aria-label={`Read the article: ${post.title}`} style={{ display: 'block' }}>
                <div style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 16, aspectRatio: '16/9', position: 'relative', background: '#ddd' }}>
                  <img {...imgProps(post.image || stockImgs[i % stockImgs.length], 600, 338)} alt={`${post.title}, ${post.category || 'marketing'} article by Blue Cactus Digital`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: washColors[i % washColors.length], opacity: 0.65, mixBlendMode: 'multiply' }} />
                </div>
              </Link>
              <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', color: 'var(--white)', marginBottom: 14, textTransform: 'uppercase', lineHeight: 1.4 }}>
                {post.title}
              </h4>
              <Link to={`/blog/${post.slug}`} aria-label={`Read the article: ${post.title}`} className="btn-pink-sm">READ ARTICLE</Link>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 36 }}>
          <Link to="/blog" className="btn-pink-filled" data-testid="view-all-blogs-btn">
            VIEW ALL BLOGS <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const [guideModal, setGuideModal] = useState(null);
  return (
    <div data-testid="homepage">
      <PageSeo path="/" />
      <JsonLd id="org" data={organizationSchema} />
      {/* === HERO VIDEO SECTION === */}
      <section style={{ paddingTop: 62, background: '#000' }}>
        <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', overflow: 'hidden' }}>
          <video
            src="https://customer-assets-0z36b82j.emergentagent.net/job_4615c39b-1faa-495f-81e4-3cbdebe05d33/artifacts/bfe3d4c3769f2e3f_blue_cactus_homepage_v2_1080p.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', border: 'none' }}
            data-testid="hero-video"
          />
        </div>
      </section>

      {/* === CONVERSION CTA BAND (primary lead-gen entry points) === */}
      <section data-testid="home-conversion-band" style={{ background: 'var(--teal)', padding: '52px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.6rem, 3.4vw, 2.6rem)', color: '#fff', lineHeight: 1.25, marginBottom: 14, textTransform: 'uppercase', fontFamily: 'var(--font-heading)' }}>
            Turn your website into your best source of enquiries
          </h2>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.92)', maxWidth: 620, margin: '0 auto 30px', lineHeight: 1.6 }}>
            Get a free, personalised marketing plan and quote for your health or care organisation, in about 3 minutes. No cost, no obligation.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/quiz" className="btn-pink-filled" data-testid="home-band-quote-cta">
              <Sparkles size={16} /> GET MY FREE PLAN &amp; QUOTE
            </Link>
            <Link to="/marketing-health-check" data-testid="home-band-healthcheck-cta" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px',
              background: 'rgba(255,255,255,0.12)', border: '2px solid rgba(255,255,255,0.55)', color: '#fff',
              fontFamily: 'var(--font-heading)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              <Activity size={16} /> TAKE THE 60-SECOND HEALTH CHECK
            </Link>
          </div>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.85)', marginTop: 22, letterSpacing: '0.02em' }}>
            Trusted by 70+ health, care &amp; purpose-led organisations
          </p>
        </div>
      </section>

      {/* === "WE AMPLIFY CHANGE" + "THROUGH MEANINGFUL MESSAGES" SECTION === */}
      <section style={{ background: 'var(--white)', padding: '80px 0 56px' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px' }}>
          {/* Headings with background blocks */}
          <div style={{ marginBottom: 60 }}>
            <div className="fade-in-up" style={{ marginBottom: 12 }}>
              <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', lineHeight: 1.3 }}>
                <span className="heading-block-purple">MARKETING THAT HELPS PEOPLE</span>
              </h1>
            </div>
            <div className="fade-in-up fade-in-up-delay-1" style={{ textAlign: 'right' }}>
              <p aria-hidden="true" style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', lineHeight: 1.3, fontFamily: 'var(--font-heading)', fontWeight: 400 }}>
                <span className="heading-block-pink">LIVE HAPPIER, HEALTHIER LIVES</span>
              </p>
            </div>
          </div>

          {/* Content grid: cactus illustrations left, text right */}
          <div className="hero-content-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'stretch' }}>
            <div className="hero-cactus-col fade-in-up fade-in-up-delay-2" style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/j6qymf0j_three-cacti-with-bg-pink.png)',
              backgroundPosition: 'center bottom',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              minHeight: 520,
              alignSelf: 'end',
            }} />
            <div className="fade-in-up fade-in-up-delay-2" style={{ alignSelf: 'start' }}>
              {heroContent.paragraphs.map((p, i) => (
                <p key={i} style={{ fontSize: '18px', marginBottom: 20, maxWidth: 580, color: 'var(--body-text)' }}>{p}</p>
              ))}
              <div style={{ marginTop: 32 }}>
                <a
                  href={heroContent.ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pink-filled"
                  data-testid="hero-cta-btn"
                >
                  {heroContent.ctaText} <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === SOCIAL ICONS BAR === */}
      <section style={{ background: 'var(--dark)', padding: '24px 0' }} data-testid="social-icons-bar">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 56 }}>
          {[
            { icon: <Instagram size={32} />, url: siteConfig.social.instagram, label: 'Instagram' },
            { icon: <Facebook size={32} />, url: siteConfig.social.facebook, label: 'Facebook' },
            { icon: <Linkedin size={32} />, url: siteConfig.social.linkedin, label: 'LinkedIn' },
            { icon: <TikTokIcon size={32} />, url: siteConfig.social.tiktok, label: 'TikTok' },
          ].map((s, i) => (
            <a
              key={i}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              data-testid={`social-${s.label.toLowerCase()}`}
              style={{ color: 'rgba(255,255,255,0.9)', transition: 'color 0.25s ease, transform 0.25s ease', display: 'flex' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--hot-pink)'; e.currentTarget.style.transform = 'scale(1.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {s.icon}
            </a>
          ))}
        </div>
      </section>

      {/* === CLIENT LOGOS CAROUSEL === */}
      <section style={{ padding: '56px 0', background: 'var(--white)', overflow: 'hidden' }} data-testid="logo-carousel">
        <div style={{ overflow: 'hidden' }}>
          <div className="logo-marquee-track">
            {[...clientLogos, ...clientLogos].map((logo, i) => (
              <div key={i} style={{ flex: '0 0 auto', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={logo.url}
                  alt={`${logo.name} logo`}
                  loading="lazy"
                  width="260"
                  height="100"
                  style={{ height: 100, maxWidth: 260, objectFit: 'contain' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === RESULTS WALL (proof) === */}
      <ResultsWall theme="dark" />

      {/* === WHO WE HELP (sectors, charities & care-tech first) === */}
      <section data-testid="home-who-we-help" style={{ background: 'var(--white)', padding: '80px 0' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ maxWidth: 720, marginBottom: 44 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', lineHeight: 1.2 }}>
              <span className="heading-block-purple">WHO WE HELP</span>
            </h2>
            <p style={{ fontSize: 18, color: 'var(--body-text)', lineHeight: 1.7, marginTop: 18 }}>
              We specialise in charities, care technology and healthtech, and purpose-led health and social care organisations, bringing rare sector knowledge to every campaign.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
            {orderedHomeSectors.map((s) => (
              <Link key={s.slug} to={`/sectors/${s.slug}`} data-testid={`home-sector-${s.slug}`}
                style={{ display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--off-white)', border: '1px solid #e6e6e6', borderRadius: 12, padding: '28px 26px', textDecoration: 'none', transition: 'transform 0.25s ease, box-shadow 0.25s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18, textTransform: 'uppercase', color: 'var(--dark)', lineHeight: 1.2 }}>{s.navLabel}</span>
                <span style={{ fontSize: 18, color: 'var(--body-text)', lineHeight: 1.6, flex: 1 }}>{SECTOR_TAGLINES[s.slug]}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-heading)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--teal)' }}>Explore {s.navLabel} marketing <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* === WHAT WE OFFER SECTION === */}
      <section style={{ position: 'relative', background: 'var(--yellow)', paddingTop: 80 }} data-testid="what-we-offer">
        {/* Shape divider at top */}
        <div className="shape-divider-top">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2600 131.1" preserveAspectRatio="none" style={{ height: 80 }}>
            <path fill="#FFFFFF" d="M0 0L2600 0 2600 69.1 0 0z" />
            <path fill="#FFFFFF" style={{ opacity: 0.5 }} d="M0 0L2600 0 2600 69.1 0 69.1z" />
            <path fill="#FFFFFF" style={{ opacity: 0.25 }} d="M2600 0L0 0 0 130.1 2600 69.1z" />
          </svg>
        </div>

        <div style={{ textAlign: 'center', padding: '0 48px 40px' }}>
          <SectionReveal>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.3, display: 'inline-block' }}>
              <span className="heading-block-pink">WHAT WE OFFER</span>
            </h2>
          </SectionReveal>
        </div>

        {/* === SERVICE ROWS === */}
        {services.map((service, i) => (
          <div
            key={i}
            className="service-row"
            data-testid={`service-row-${i}`}
            style={{
              display: 'flex',
              width: '100%',
              minHeight: 530,
              background: serviceRowBgs[i],
            }}
          >
            {/* Left: Cactus illustration background */}
            <div
              className="service-cactus-bg"
              style={{
                width: '40%',
                backgroundImage: `url(${cactusBgImages[i]})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center bottom',
                backgroundRepeat: 'no-repeat',
              }}
            />

            {/* Center: Step number circle with vertical line */}
            <div
              className="service-step-col"
              style={{
                width: '10%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              {/* Vertical teal line */}
              <div style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: 3,
                background: 'var(--teal)',
                transform: 'translateX(-50%)',
                zIndex: 0,
              }} />
              <div style={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'var(--white)',
                border: '3px solid var(--teal)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                position: 'relative',
              }}>
                <span style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '2.8rem',
                  color: 'var(--teal)',
                  lineHeight: 1,
                }}>{i + 1}</span>
              </div>
            </div>

            {/* Right: Image + Title + Description */}
            <div
              className="service-content-col"
              style={{
                width: '50%',
                padding: '50px 48px 50px 24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <div style={{ marginBottom: 20 }}>
                <img
                  src={service.image}
                  alt={service.title}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', maxWidth: 700, display: 'block' }}
                />
              </div>
              <h3 style={{
                fontSize: 'clamp(2rem, 3.5vw, 3rem)',
                color: serviceTitleColors[i],
                marginBottom: 16,
                textTransform: 'uppercase',
                lineHeight: 0.95,
              }}>
                {service.title}
              </h3>
              <p style={{ fontSize: '18px', maxWidth: 520, lineHeight: 1.8 }}>
                {service.description}
              </p>
              <div style={{ marginTop: 24 }}>
                <Link to={service.link} className="btn-pink-filled" style={{ display: 'inline-flex' }}>
                  FIND OUT MORE <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* === GET IN TOUCH CTA === */}
      <section className="cactus-bg-section" style={{ 
        background: 'var(--white)', 
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/uek70qqw_cactus-pink-vase-doodles.png)',
        backgroundPosition: 'bottom right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        padding: '80px 0' 
      }} data-testid="get-in-touch">
        <div className="get-in-touch-grid" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
          <SectionReveal>
            <div style={{ background: 'rgba(255,255,255,0.92)', padding: '32px 24px' }}>
              <h5 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
                marginBottom: 20,
                textTransform: 'uppercase',
                lineHeight: 1.1,
              }}>
                <span style={{ background: 'var(--teal-dark)', color: 'var(--white)', padding: '8px 16px', display: 'inline' }}>GET IN TOUCH</span>
              </h5>
              <p style={{ fontSize: '18px', marginBottom: 28, lineHeight: 1.8 }}>
                Ready to grow your reach in health and social care? Get in touch with{' '}
                <strong style={{ color: 'var(--dark)' }}>Blue Cactus Digital</strong> to talk about how we can help you connect with the people who need you most. Together, we'll shape marketing that communicates your value with warmth, clarity and impact.
              </p>
              <Link to="/contact" className="btn-pink-filled" data-testid="contact-us-cta">
                <ArrowRight size={16} /> CONTACT US
              </Link>
            </div>
          </SectionReveal>
          <div />
        </div>
      </section>

      {/* === BLOG POSTS SECTION === */}
      <BlogPreviewSection />

      {/* === FREE GUIDES STRIP === */}
      <section style={{ background: 'var(--off-white)', padding: '70px 0' }} data-testid="home-free-guides">
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 30 }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--dark)', color: '#fff', padding: '6px 14px', borderRadius: 20, fontFamily: 'var(--font-heading)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}><FileDown size={14} /> Free downloads</div>
              <h2 style={{ fontSize: 'clamp(1.6rem, 3.4vw, 2.4rem)', lineHeight: 1.15, color: 'var(--dark)', margin: 0, textTransform: 'uppercase', fontFamily: 'var(--font-heading)' }}>Free guides &amp; templates</h2>
              <p style={{ fontSize: 18, color: 'var(--body-text)', lineHeight: 1.6, marginTop: 12, maxWidth: 640 }}>Practical, no-jargon playbooks, checklists and templates for charities, care providers and care technology, download them free.</p>
            </div>
            <Link to="/resources" className="btn-pink-filled" data-testid="home-free-guides-cta" style={{ flexShrink: 0 }}>BROWSE ALL FREE GUIDES <ArrowRight size={14} /></Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 22 }}>
            {[
              { slug: 'google-ad-grant-ultimate-guide', audience: 'For charities', title: 'The Ultimate Guide to the Google Ad Grant', blurb: 'Claim and manage up to $10,000/month of free Google ads for your charity.' },
              { slug: 'care-tech-marketing-ultimate-guide', audience: 'For care technology', title: 'The Ultimate Guide to Marketing Care Technology', blurb: 'Win NHS and local authority deals with a predictable demand engine.' },
              { slug: 'care-home-marketing-ultimate-guide', audience: 'For care providers', title: 'The Ultimate Guide to Marketing Your Care Home', blurb: 'Fill beds, win contracts and recruit carers with sector-savvy marketing.' },
            ].map((g, i) => (
              <button key={i} onClick={() => setGuideModal({ slug: g.slug, title: g.title })} data-testid={`home-guide-card-${i}`} aria-label={`Download the free guide: ${g.title}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', textDecoration: 'none', background: '#fff', border: '1px solid #e6e6e6', borderTop: '4px solid var(--hot-pink)', borderRadius: 12, padding: '26px 24px', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--teal)', marginBottom: 10 }}>{g.audience}</span>
                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, color: 'var(--dark)', marginBottom: 10, lineHeight: 1.3 }}>{g.title}</h3>
                <p style={{ fontSize: 18, color: 'var(--body-text)', lineHeight: 1.6, marginBottom: 16, flex: 1 }}>{g.blurb}</p>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-heading)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--hot-pink)' }}><FileDown size={14} /> Download free</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* === CERTIFICATIONS BAR === */}
      <section style={{ background: 'var(--dark)', padding: '48px 0' }} data-testid="certifications-bar">
        <div className="cert-logos" style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 40, padding: '0 48px', flexWrap: 'wrap' }}>
          {[
            { name: 'Living Wage Employer', src: 'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/pem95tqf_LW_Employer_0.png', invert: false },
            { name: 'Clean Creatives', src: 'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/cs132gn3_Clean-Creatives-SealTRANSPARENT-BLACK1080-1-600x600.webp', invert: true },
            { name: 'Cyber Essentials', src: 'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/6kp6rz82_Cyber-Essentials-Certified-logo-768x694.png', invert: false },
            { name: 'CIM', src: 'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/p34xr6wc_pngwing.com_-1-1-768x446.png', invert: true },
            { name: 'Sustainable Creative Charter', src: 'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/8m9k9li4_sustainable-creative-charter-OUTLINE_2-768x773.webp', invert: true },
          ].map((cert, i) => (
            <img
              key={i}
              src={cert.src}
              alt={cert.name}
              style={{ height: 90, objectFit: 'contain', filter: cert.invert ? 'brightness(0) invert(1)' : 'none', opacity: 0.9 }}
            />
          ))}
        </div>
      </section>
      {guideModal && <ResourceGateModal resource={guideModal} onClose={() => setGuideModal(null)} />}
    </div>
  );
};

export default HomePage;
